import { CircularProgress, Typography } from '@mui/material';
import clsx from 'clsx';
import React, { memo, useMemo } from 'react';
import { AxisDomain } from 'recharts/types/util/types';

import { BarChart } from '~/components/BarChart';
import { useGetCompanyAnalyticsQuery } from '~/services/rtkQuery/company';

import styles from './styles.module.scss';

const Y_AXIS_DOMAIN: AxisDomain = ['dataMin', 'dataMax'];
const ROUND_A_PRE_MONEY_X_AXIS_DOMAIN = [0, 600];
const ROUND_A_DEAL_SIZE_X_AXIS_DOMAIN = [0, 250];
const ROUND_A_SHARE_SOLD_X_AXIS_DOMAIN = [0, 2.5];
const ROUND_B_PRE_MONEY_X_AXIS_DOMAIN = [0, 2000];
const ROUND_B_DEAL_SIZE_X_AXIS_DOMAIN = [0, 600];
const ROUND_B_SHARE_SOLD_X_AXIS_DOMAIN = [0, 2];
const ROUND_C_PRE_MONEY_X_AXIS_DOMAIN = [0, 6000];
const ROUND_C_DEAL_SIZE_X_AXIS_DOMAIN = [0, 900];
const ROUND_C_SHARE_SOLD_X_AXIS_DOMAIN = [0, 0.8];

type Props = Record<
  'preMoney' | 'dealSize' | 'shareSold',
  {
    a: number;
    b: number;
    c: number;
  }
>;

const Component: React.FC<Props> = ({ preMoney, dealSize, shareSold }) => {
  const getCompanyAnalyticsQuery = useGetCompanyAnalyticsQuery();

  const roundABarChartData = useMemo(() => {
    const data = getCompanyAnalyticsQuery.data?.result.data.a;

    return {
      preMoney:
        data?.pre_money.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.pre_money.counts[index],
          }))
          .slice(0, -1) || [],
      dealSize:
        data?.deal_size.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.deal_size.counts[index],
          }))
          .slice(0, -1) || [],
      shareSold:
        data?.share_sold.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.share_sold.counts[index],
          }))
          .slice(0, -1) || [],
      preMoneyLines: data
        ? [
            {
              x: data.pre_money.round_mean,
              y: Math.max(...data.pre_money.counts),
              type: 'success' as const,
            },
            {
              x: preMoney.a,
              y: Math.max(...data.pre_money.counts),
              type: 'error' as const,
            },
          ]
        : [],
      dealSizeLines: data
        ? [
            {
              x: data.deal_size.round_mean,
              y: Math.max(...data.deal_size.counts),
              type: 'success' as const,
            },
            {
              x: dealSize.a,
              y: Math.max(...data.deal_size.counts),
              type: 'error' as const,
            },
          ]
        : [],
      shareSoldLines: data
        ? [
            {
              x: data.share_sold.round_mean,
              y: Math.max(...data.share_sold.counts),
              type: 'success' as const,
            },
            {
              x: shareSold.a,
              y: Math.max(...data.share_sold.counts),
              type: 'error' as const,
            },
          ]
        : [],
    };
  }, [dealSize.a, getCompanyAnalyticsQuery.data?.result.data.a, preMoney.a, shareSold.a]);

  const roundBBarChartData = useMemo(() => {
    const data = getCompanyAnalyticsQuery.data?.result.data.b;

    return {
      preMoney:
        data?.pre_money.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.pre_money.counts[index],
          }))
          .slice(0, -1) || [],
      dealSize:
        data?.deal_size.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.deal_size.counts[index],
          }))
          .slice(0, -1) || [],
      shareSold:
        data?.share_sold.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.share_sold.counts[index],
          }))
          .slice(0, -1) || [],
      preMoneyLines: data
        ? [
            {
              x: data.pre_money.round_mean,
              y: Math.max(...data.pre_money.counts),
              type: 'success' as const,
            },
            {
              x: preMoney.b,
              y: Math.max(...data.pre_money.counts),
              type: 'error' as const,
            },
          ]
        : [],
      dealSizeLines: data
        ? [
            {
              x: data.deal_size.round_mean,
              y: Math.max(...data.deal_size.counts),
              type: 'success' as const,
            },
            {
              x: dealSize.b,
              y: Math.max(...data.deal_size.counts),
              type: 'error' as const,
            },
          ]
        : [],
      shareSoldLines: data
        ? [
            {
              x: data.share_sold.round_mean,
              y: Math.max(...data.share_sold.counts),
              type: 'success' as const,
            },
            {
              x: shareSold.b,
              y: Math.max(...data.share_sold.counts),
              type: 'error' as const,
            },
          ]
        : [],
    };
  }, [dealSize.b, getCompanyAnalyticsQuery.data?.result.data.b, preMoney.b, shareSold.b]);

  const roundCBarChartData = useMemo(() => {
    const data = getCompanyAnalyticsQuery.data?.result.data.c;

    return {
      preMoney:
        data?.pre_money.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.pre_money.counts[index],
          }))
          .slice(0, -1) || [],
      dealSize:
        data?.deal_size.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.deal_size.counts[index],
          }))
          .slice(0, -1) || [],
      shareSold:
        data?.share_sold.bin_edges
          .map((bin, index, array) => ({
            x: (bin + array[index + 1]) / 2,
            y: data.share_sold.counts[index],
          }))
          .slice(0, -1) || [],
      preMoneyLines: data
        ? [
            {
              x: data.pre_money.round_mean,
              y: Math.max(...data.pre_money.counts),
              type: 'success' as const,
            },
            {
              x: preMoney.c,
              y: Math.max(...data.pre_money.counts),
              type: 'error' as const,
            },
          ]
        : [],
      dealSizeLines: data
        ? [
            {
              x: data.deal_size.round_mean,
              y: Math.max(...data.deal_size.counts),
              type: 'success' as const,
            },
            {
              x: dealSize.c,
              y: Math.max(...data.deal_size.counts),
              type: 'error' as const,
            },
          ]
        : [],
      shareSoldLines: data
        ? [
            {
              x: data.share_sold.round_mean,
              y: Math.max(...data.share_sold.counts),
              type: 'success' as const,
            },
            {
              x: shareSold.c,
              y: Math.max(...data.share_sold.counts),
              type: 'error' as const,
            },
          ]
        : [],
    };
  }, [dealSize.c, getCompanyAnalyticsQuery.data?.result.data.c, preMoney.c, shareSold.c]);

  if (getCompanyAnalyticsQuery.isLoading) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!getCompanyAnalyticsQuery.data) {
    return null;
  }

  const roundAData = getCompanyAnalyticsQuery.data.result.data.a;
  const roundBData = getCompanyAnalyticsQuery.data.result.data.b;
  const roundCData = getCompanyAnalyticsQuery.data.result.data.c;

  return (
    <div className={styles.container}>
      <Typography
        color="text.primary"
        fontWeight="700"
        lineHeight="19px"
        mb="25px"
        variant="subtitle1"
      >
        Pre-Money vs Dataset
      </Typography>
      <div className={styles.container__charts}>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round A
          </Typography>
          <BarChart
            data={roundABarChartData.preMoney}
            lines={roundABarChartData.preMoneyLines}
            rightBottomLabel={`Company = ${preMoney.a.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundAData.pre_money.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_A_PRE_MONEY_X_AXIS_DOMAIN}
            xAxisTickCount={7}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={4}
          />
        </div>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round B
          </Typography>
          <BarChart
            data={roundBBarChartData.preMoney}
            lines={roundBBarChartData.preMoneyLines}
            rightBottomLabel={`Company = ${preMoney.b.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundBData.pre_money.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_B_PRE_MONEY_X_AXIS_DOMAIN}
            xAxisTickCount={5}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={5}
          />
        </div>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round C
          </Typography>
          <BarChart
            data={roundCBarChartData.preMoney}
            lines={roundCBarChartData.preMoneyLines}
            rightBottomLabel={`Company = ${preMoney.c.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundCData.pre_money.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_C_PRE_MONEY_X_AXIS_DOMAIN}
            xAxisTickCount={7}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={7}
          />
        </div>
      </div>
      <Typography
        color="text.primary"
        fontWeight="700"
        lineHeight="19px"
        mb="25px"
        variant="subtitle1"
      >
        Money Raised vs Dataset
      </Typography>
      <div className={styles.container__charts}>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round A
          </Typography>
          <BarChart
            data={roundABarChartData.dealSize}
            lines={roundABarChartData.dealSizeLines}
            rightBottomLabel={`Company = ${dealSize.a.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundAData.deal_size.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_A_DEAL_SIZE_X_AXIS_DOMAIN}
            xAxisTickCount={6}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={8}
          />
        </div>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round B
          </Typography>
          <BarChart
            data={roundBBarChartData.dealSize}
            lines={roundBBarChartData.dealSizeLines}
            rightBottomLabel={`Company = ${dealSize.b.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundBData.deal_size.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_B_DEAL_SIZE_X_AXIS_DOMAIN}
            xAxisTickCount={7}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={8}
          />
        </div>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round C
          </Typography>
          <BarChart
            data={roundCBarChartData.dealSize}
            lines={roundCBarChartData.dealSizeLines}
            rightBottomLabel={`Company = ${dealSize.c.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundCData.deal_size.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_C_DEAL_SIZE_X_AXIS_DOMAIN}
            xAxisTickCount={10}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={10}
          />
        </div>
      </div>
      <Typography
        color="text.primary"
        fontWeight="700"
        lineHeight="19px"
        mb="25px"
        variant="subtitle1"
      >
        Share Sold vs Dataset
      </Typography>
      <div className={clsx(styles.container__charts, styles['container__charts--last'])}>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round A
          </Typography>
          <BarChart
            data={roundABarChartData.shareSold}
            lines={roundABarChartData.shareSoldLines}
            rightBottomLabel={`Company = ${shareSold.a.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundAData.share_sold.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_A_SHARE_SOLD_X_AXIS_DOMAIN}
            xAxisTickCount={8}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={10}
          />
        </div>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round B
          </Typography>
          <BarChart
            data={roundBBarChartData.shareSold}
            lines={roundBBarChartData.shareSoldLines}
            rightBottomLabel={`Company = ${shareSold.b.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundBData.share_sold.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_B_SHARE_SOLD_X_AXIS_DOMAIN}
            xAxisTickCount={5}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={6}
          />
        </div>
        <div className={styles.container__chart}>
          <Typography color="var(--text-3-color)" lineHeight="19px" mb="15px" variant="subtitle1">
            Round C
          </Typography>
          <BarChart
            data={roundCBarChartData.shareSold}
            lines={roundCBarChartData.shareSoldLines}
            rightBottomLabel={`Company = ${shareSold.c.toFixed(2)}`}
            rightTopLabel={`Mean = ${roundCData.share_sold.round_mean.toFixed(2)}`}
            xAxisDomain={ROUND_C_SHARE_SOLD_X_AXIS_DOMAIN}
            xAxisTickCount={9}
            yAxisDomain={Y_AXIS_DOMAIN}
            yAxisTickCount={6}
          />
        </div>
      </div>
    </div>
  );
};

export const Analytics = memo(Component);
